import { Link } from "react-router-dom";
import { ShieldCheck, Heart, Compass, ArrowRight } from "lucide-react";
import { PageHero, CTABand, StatCounter } from "../components/sections";
import { Reveal } from "../components/Reveal";
import { TIMELINE, STATS, CERT_LOGOS, IMAGES, COMPANY } from "../data/site";

const VALUES = [
  { icon: Heart, title: "Family First", desc: "We treat every traveller like family — the same warmth that started it all in 1991." },
  { icon: ShieldCheck, title: "Trust & Transparency", desc: "Honest pricing, clear itineraries and no hidden surprises. Ever." },
  { icon: Compass, title: "Genuine Expertise", desc: "Three decades of on-ground knowledge across India and the world." },
];

export default function About() {
  return (
    <div>
      <PageHero eyebrow="Our Story" title="Creating memories since 1991" subtitle="A family-run travel house from Pune, built on trust, warmth and three decades of wanderlust." image={IMAGES.himalaya} />

      <section className="ll-container ll-section grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <img src={IMAGES.planning} alt="Planning bespoke journeys at Life Line" className="rounded-[2rem] w-full h-[500px] object-cover" />
        </Reveal>
        <Reveal delay={0.15}>
          <p className="eyebrow text-[#f7ad2d]">The Founders</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 leading-tight">From a car rental to a travel legacy</h2>
          <p className="text-[#4A5D58] text-lg mt-6 leading-relaxed">
            In 1991, <strong className="text-[#0A221C]">Mr. Jeevan Hendre</strong> and <strong className="text-[#0A221C]">Mrs. Rekha Hendre</strong> started Life Line as a small car rental company in Pune. Their promise was simple — reliability and genuine care.
          </p>
          <p className="text-[#4A5D58] text-lg mt-4 leading-relaxed">
            That promise turned every customer into a repeat traveller, and Life Line grew into a complete one-stop travel solution serving over 3000 travellers every year. Today, the family legacy continues with the same personal touch that defined day one.
          </p>
        </Reveal>
      </section>

      {/* Stats */}
      <section className="ll-container pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-12 border-y border-black/5">
          {STATS.map((s) => <StatCounter key={s.label} {...s} />)}
        </div>
      </section>

      {/* Timeline */}
      <section className="ll-container ll-section">
        <Reveal className="text-center max-w-2xl mx-auto mb-16">
          <p className="eyebrow text-[#f7ad2d]">Our Journey</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">1991 → Today</h2>
        </Reveal>
        <div className="relative max-w-3xl mx-auto border-l-2 border-[#4ef72d]/30 pl-8 md:pl-12 space-y-12">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.05}>
              <div className="relative">
                <span className="absolute -left-[42px] md:-left-[58px] top-1 w-5 h-5 rounded-full bg-[#4ef72d] ring-4 ring-[#4ef72d]/20" />
                <p className="font-display text-3xl font-bold text-[#f7ad2d]">{t.year}</p>
                <h3 className="font-display text-2xl font-bold text-[#0A221C] mt-1">{t.title}</h3>
                <p className="text-[#4A5D58] mt-2 leading-relaxed">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#0A221C] relative overflow-hidden">
        <div className="grain" />
        <div className="ll-container ll-section relative">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow text-[#4ef72d]">What We Stand For</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3">Our values</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="border border-white/10 rounded-3xl p-8 h-full">
                  <div className="w-14 h-14 rounded-2xl bg-[#4ef72d]/15 flex items-center justify-center mb-5"><v.icon size={26} className="text-[#4ef72d]" /></div>
                  <h3 className="font-display text-2xl font-bold text-white">{v.title}</h3>
                  <p className="text-white/65 mt-3 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="ll-container ll-section text-center">
        <Reveal>
          <p className="eyebrow text-[#f7ad2d]">Recognised & Trusted</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-10">Certifications & Affiliations</h2>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
            {CERT_LOGOS.map((c) => (
              <div key={c.name} className="bg-white rounded-2xl px-8 py-6 shadow-[0_8px_24px_rgba(10,34,28,0.07)] ring-1 ring-black/5 flex items-center justify-center h-24 md:h-28">
                <img src={c.url} alt={c.name} title={c.name} className="h-14 md:h-20 w-auto object-contain" />
              </div>
            ))}
          </div>
          <Link to="/contact" className="btn-primary mt-12">Plan a Trip With Us <ArrowRight size={18} /></Link>
        </Reveal>
      </section>

      <div className="pb-24"><CTABand /></div>
    </div>
  );
}
